import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import React from 'react';

import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import UnoButton from '../../../../../utils/UnoButton';

const useStyles = makeStyles({
    row: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        border: 'solid 1px ' + unoTheme.borderInputColor,
        borderRadius: '10px',
        padding: '10px 16px',
        marginBottom: '8px',
    },
    statusField: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
    },
    infoField: {
        flex: 1,
        minWidth: 0,
    },
    name: {
        fontSize: '13px',
        fontWeight: 600,
        color: unoTheme.defaultTextColor,
    },
    //Linha secundária: reúne num texto só o que antes eram duas colunas (gerado / assinado)
    state: {
        fontSize: '11px',
        marginTop: '2px',
    },
    statePending: {
        color: unoTheme.warningColorStrong,
    },
    stateSigned: {
        color: unoTheme.greenMoney,
        //O nome do arquivo pode ser longo; corta em vez de empurrar as ações
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actionsField: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        whiteSpace: 'nowrap',
    },
});

/**
 * Uma linha de documento na etapa 2.
 *
 * Layout em flex com borda, e não em tabela: são até cinco botões de ação por linha, e a coluna de
 * ações da tabela vivia presa numa largura fixa para caber todos.
 *
 * O `<input type="file">` NÃO vive aqui: é um só, no componente pai, coordenado por
 * `documentIdToUpload`. Cada linha só avisa que quer anexar, via `onUploadSigned`.
 *
 * A prop `document` sombreia o `document` global do navegador. Aqui não há uso do DOM, então é
 * seguro — é por isso que o pai escreve `window.document.createElement` no download.
 *
 * @param {{
 *   document: {
 *     name: string, generated_key: string, signed_key: string, signed_file_name: string,
 *   },
 *   canEdit: boolean,
 *   onGenerate: () => void,
 *   onDownloadGenerated: () => void,
 *   onUploadSigned: () => void,
 *   onDownloadSigned: () => void,
 *   onRemoveSigned: () => void,
 * }} props
 */
export default function TrademateDocumentRow(props) {

    const {
        document, canEdit,
        onGenerate, onDownloadGenerated, onUploadSigned, onDownloadSigned, onRemoveSigned,
    } = props;

    const classes = useStyles();

    const isGenerated = !!document.generated_key;
    const isSigned = !!document.signed_key;

    const renderState = () => {

        if (isSigned) {
            return (
                <Typography align="left" className={[classes.state, classes.stateSigned].join(' ')}>
                    {'Assinado: ' + (document.signed_file_name ? document.signed_file_name : '-')}
                </Typography>
            );
        }

        return (
            <Typography align="left" className={[classes.state, classes.statePending].join(' ')}>
                {isGenerated ? 'Gerado · assinatura pendente' : 'Ainda não gerado'}
            </Typography>
        );
    };

    return (
        <div className={classes.row}>

            <div className={classes.statusField}>
                {isSigned
                    ? <CheckCircleIcon style={{ color: unoTheme.successColor }} />
                    : <RadioButtonUncheckedIcon style={{ color: unoTheme.neutralIconColor }} />}
            </div>

            <div className={classes.infoField}>
                <Typography align="left" className={classes.name}>{document.name}</Typography>
                {renderState()}
            </div>

            <div className={classes.actionsField}>

                {canEdit && (
                    <UnoButton
                        size='small'
                        type={isGenerated ? 'primaryClean' : 'primary'}
                        startIcon={<DescriptionIcon />}
                        onClick={onGenerate}>
                        {isGenerated ? 'Gerar novamente' : 'Gerar'}
                    </UnoButton>
                )}

                {/* Baixar o gerado não exige canEdit: quem só lê também precisa do documento.
                    Os dois botões de baixar usam o mesmo ícone, então o rótulo é o que distingue
                    o documento gerado do assinado. */}
                {isGenerated && (
                    <UnoButton
                        size='small'
                        type='success'
                        startIcon={<GetAppIcon />}
                        onClick={onDownloadGenerated}>
                        {'Baixar'}
                    </UnoButton>
                )}

                {canEdit && isGenerated && (
                    <UnoButton
                        size='small'
                        type='primary'
                        startIcon={<PublishIcon />}
                        onClick={onUploadSigned}>
                        {/* Numa linha que já tem assinado, "Anexar" descreveria errado o clique */}
                        {isSigned ? 'Substituir' : 'Anexar assinado'}
                    </UnoButton>
                )}

                {isSigned && (
                    <UnoButton
                        size='small'
                        type='successClean'
                        startIcon={<GetAppIcon />}
                        onClick={onDownloadSigned}>
                        {'Baixar assinado'}
                    </UnoButton>
                )}

                {canEdit && isSigned && (
                    <UnoButton
                        isIconButton
                        removeDefaultLeftMargin
                        size='small'
                        type='error'
                        myIcon={<DeleteIcon />}
                        onClick={onRemoveSigned}
                    />
                )}

            </div>

        </div>
    );
}
